import { JsmClientError } from '../errors';
import { type ApiMediaErrorResponseDto, type JsmErrorResponseDto } from '../types';

interface RequestOptions {
  headers?: {
    Authorization?: string;
    'Content-Type'?: string;
    'Content-Language'?: string;
  };
  credentials?: RequestCredentials;
}

interface FetchParameters {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  url: string;
  body?: Record<string, unknown> | FormData;
  options?: RequestOptions;
}

export class JsmClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/^https?:\/\//, '');
  }

  private async fetchExtended<T>({ method, url, body, options }: FetchParameters): Promise<T> {
    const headers = body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : undefined;
    const response = await fetch(this.getAbsoluteUrl(url), {
      method,
      ...(body && {
        body: body instanceof FormData ? body : JSON.stringify(body),
      }),
      ...options,
      headers: { ...headers, ...options?.headers },
    });

    return await this.handleResponse(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    let contentType = response.headers.get('Content-Type');
    if (contentType) {
      // content-type can be as well: application/json;charset=UTF-8
      contentType = contentType.split(';')[0].toLowerCase();
    }

    if (response.ok) {
      const responseData = contentType === 'application/json' ? await response.json() : await response.text();
      if (!responseData) {
        return undefined as T;
      }
      // append content of X-Xsrf-Token to json response if possible
      const xsrfToken = response.headers.get('X-Xsrf-Token');
      if (xsrfToken && contentType === 'application/json') {
        responseData.xsrfToken = xsrfToken;
      }
      return responseData as T;
    } else {
      let jsmError, responseText;
      try {
        responseText = await response.text();
        jsmError =
          contentType === 'application/json'
            ? (JSON.parse(responseText) as JsmErrorResponseDto | ApiMediaErrorResponseDto)
            : responseText;
      } catch {
        jsmError = responseText ?? response.statusText;
      }
      console.error('JsmClient:', jsmError);
      throw new JsmClientError(response.status, jsmError);
    }
  }

  private handleXhrResponse<T>(xhr: XMLHttpRequest): T {
    let contentType = xhr.getResponseHeader('Content-Type');
    if (contentType) {
      contentType = contentType.split(';')[0].toLowerCase();
    }

    if (xhr.readyState === 4 && xhr.status === 200) {
      const responseData = contentType === 'application/json' ? JSON.parse(xhr.responseText) : xhr.responseText;
      if (!responseData) {
        return undefined as T;
      }
      return responseData as T;
    } else {
      let jsmError;
      try {
        jsmError = JSON.parse(xhr.responseText) as JsmErrorResponseDto;
      } catch {
        jsmError = xhr.responseText;
      }
      console.error('JsmClient:', jsmError);
      throw new JsmClientError(xhr.status, jsmError);
    }
  }

  public get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.fetchExtended<T>({ url, method: 'GET', options });
  }

  public post<T>(url: string, body?: Record<string, unknown> | FormData, options?: RequestOptions): Promise<T> {
    return this.fetchExtended({ url, method: 'POST', body, options });
  }

  public put<T>(url: string, body?: Record<string, unknown> | FormData, options?: RequestOptions): Promise<T> {
    return this.fetchExtended({ url, method: 'PUT', body, options });
  }

  public delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.fetchExtended({ url, method: 'DELETE', options });
  }

  private getAbsoluteUrl(relativeUrl: string): string {
    return `https://${this.baseUrl}${relativeUrl}`;
  }

  public uploadFiles<T>(url: string, files: File[], onProgress?: (progress: number) => void): Promise<T> {
    // fetch does not feature progress while posting, so use the good old XMLHttpRequest
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', e => onProgress && onProgress(e.loaded / e.total));
      xhr.addEventListener('load', () => {
        try {
          resolve(this.handleXhrResponse(xhr));
        } catch (error: any) {
          reject(error);
        }
      });
      xhr.addEventListener('error', () => {
        try {
          this.handleXhrResponse(xhr);
        } catch (error: any) {
          reject(error);
        }
      });
      xhr.addEventListener('abort', () => reject(new Error('File upload aborted')));

      // upload files as multipart post request
      xhr.open('POST', this.getAbsoluteUrl(url), true);
      const formData = new FormData();
      for (const [index, file] of Array.from(files).entries()) {
        formData.append(`file${index ? index.toString() : ''}`, file);
      }
      xhr.send(formData);
    });
  }
}
